import { Card, Flex, Text } from "@radix-ui/themes"
import type { Chart, TimeSeriesChart, BreakdownChart, MultiSeriesChart } from "@/schemas"
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  LineChart,
  Line,
} from "recharts"

interface ChartCardProps {
  chart: Chart
}

function TimeSeriesChartCard({ chart }: { chart: TimeSeriesChart }) {
  const barColor = chart.color || "var(--accent-9)"

  return (
    <Card>
      <Flex direction="column" gap="4" p="4">
        <Flex direction="column" gap="1">
          <Text size="2" weight="medium">
            {chart.label}
          </Text>
          <Text size="1" color="gray">
            {chart.event} · {chart.interval}
          </Text>
        </Flex>
        <div style={{ width: "100%", height: 250 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chart.data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="count" fill={barColor} stackId="a" />
              <Bar
                dataKey="projected_count"
                fill={barColor}
                fillOpacity={0.3}
                stackId="a"
                strokeDasharray="4 4"
                stroke={barColor}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Flex>
    </Card>
  )
}

function BreakdownChartCard({ chart }: { chart: BreakdownChart }) {
  return (
    <Card>
      <Flex direction="column" gap="4" p="4">
        <Flex direction="column" gap="1">
          <Text size="2" weight="medium">
            {chart.label}
          </Text>
        </Flex>
        <div style={{ width: "100%", height: 250 }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chart.data}
                dataKey="value"
                nameKey="label"
                cx="50%"
                cy="45%"
                outerRadius={70}
                label={({ percent }) => `${((percent as number) * 100).toFixed(0)}%`}
                labelLine={true}
              >
                {chart.data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color || "var(--accent-9)"} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => [value, "Count"]} />
              <Legend
                layout="horizontal"
                verticalAlign="bottom"
                align="center"
                wrapperStyle={{ fontSize: "12px", paddingTop: "8px" }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </Flex>
    </Card>
  )
}

function MultiSeriesChartCard({ chart }: { chart: MultiSeriesChart }) {
  // Transform series data into a format suitable for Recharts
  // Recharts expects data in the format: [{ date: "2024-01", series1: 10, series2: 20 }, ...]
  const allDates = new Set<string>()
  chart.series.forEach((s) => {
    s.data.forEach((d) => allDates.add(d.date))
  })

  const sortedDates = Array.from(allDates).sort()
  const chartData = sortedDates.map((date) => {
    const point: Record<string, string | number> = { date }
    chart.series.forEach((s) => {
      const dataPoint = s.data.find((d) => d.date === date)
      point[s.id] = dataPoint?.value ?? 0
    })
    return point
  })

  const formatYAxis = (value: number) => {
    if (chart.value_format === "percentage") {
      return `${value}%`
    }
    return String(value)
  }

  const formatTooltip = (value: string | number | readonly (string | number)[] | undefined) => {
    if (value === undefined) {
      return ""
    }

    if (chart.value_format === "percentage") {
      return `${value}%`
    }
    return value
  }

  return (
    <Card>
      <Flex direction="column" gap="4" p="4">
        <Flex direction="column" gap="1">
          <Text size="2" weight="medium">
            {chart.label}
          </Text>
        </Flex>
        <div style={{ width: "100%", height: 250 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis tickFormatter={formatYAxis} />
              <Tooltip formatter={formatTooltip} />
              <Legend
                layout="horizontal"
                verticalAlign="bottom"
                align="center"
                wrapperStyle={{ fontSize: "12px", paddingTop: "8px" }}
              />
              {chart.series.map((s) => (
                <Line
                  key={s.id}
                  type="linear"
                  dataKey={s.id}
                  name={s.label}
                  stroke={s.color || "var(--accent-9)"}
                  strokeWidth={2}
                  dot={{ r: 3, fill: s.color || "var(--accent-9)" }}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Flex>
    </Card>
  )
}

export function ChartCard({ chart }: ChartCardProps) {
  switch (chart.chart_type) {
    case "time_series":
      return <TimeSeriesChartCard chart={chart} />
    case "breakdown":
      return <BreakdownChartCard chart={chart} />
    case "multi_series":
      return <MultiSeriesChartCard chart={chart} />
    default:
      // This should never happen with proper typing
      return null
  }
}
